import React, { useState, useMemo, useCallback } from 'react';
import { useGlobalization } from '@Translations';
import {
  DataGrid,
  GridRowId,
  GridLogicOperator,
  GridSortModel,
  GridFilterModel,
  Text,
  GridRenderCellParams,
  GridRenderEditCellParams,
  CurrencyField,
  useSelectSettings,
  useUnits,
  useGridApiRef,
  error,
  success,
  getTenantId,
} from '@innovyze/stylovyze';
import { useDispatch } from 'react-redux';
import { IconButton, Menu, MenuItem, Badge } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { getCurrency } from '@innovyze/lib-am-common/Utils';
import AdjustCostDialog from '@Components/AdjustCostDialog';
import FixedValueCostDialog from '@Components/FixedValueCostDialog';
import { CostTableDetailsGridData } from '@Types/costTable.types';
import {
  applyPercentageAdjustment,
  applyFixedValueUpdate,
} from '@Utils/costCalculations';
import { CustomDataGridHeader } from './CustomDataGridHeader';
import { useCostTableTracking } from '@Hooks/useCostTableTracking';
import { useHasEditAccess } from '@Hooks/useHasEditAccess';
import {
  getLengthUnitCostColumnLabel,
  getUnitCostForClientGridOps,
  isLengthBasedCostPer,
  normalizeLengthUnitCostFromUserInput,
} from '@Utils/lengthUnitCost';

interface SingleCostTableDataGridProps {
  data: CostTableDetailsGridData[];
  onRefresh: () => void;
  onUpdatedValuesChange?: (
    updatedValues: Record<string, CostTableDetailsGridData>
  ) => void;
  updatedValues?: Record<string, CostTableDetailsGridData>;
  loading?: boolean;
  disableExport?: boolean;
  costTableId?: string;
  costTableName?: string;
  costPer?: string;
}

interface OptionsColumnProps extends GridRenderCellParams {
  onEditUnitCost: (id: string) => void;
}

const OptionsColumn: React.FC<OptionsColumnProps> = ({
  row,
  onEditUnitCost,
}) => {
  const { t } = useGlobalization();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditUnitCost = () => {
    onEditUnitCost(row.id);
    handleClose();
  };

  return (
    <>
      <IconButton
        aria-label="more"
        id={`options-button-${row.id}`}
        aria-controls={open ? `options-menu-${row.id}` : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        size="small">
        <MoreVert />
      </IconButton>
      <Menu
        id={`options-menu-${row.id}`}
        MenuListProps={{
          'aria-labelledby': `options-button-${row.id}`,
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}>
        <MenuItem onClick={handleEditUnitCost}>{t('Edit Unit Cost')}</MenuItem>
      </Menu>
    </>
  );
};

const CostTableDetailsGrid: React.FC<SingleCostTableDataGridProps> = ({
  data: propData,
  onRefresh,
  onUpdatedValuesChange,
  updatedValues: propUpdatedValues,
  loading = false,
  disableExport = false,
  costTableId,
  costTableName,
  costPer,
}) => {
  const { t } = useGlobalization();
  const dispatch = useDispatch();
  const hasEditAccess = useHasEditAccess();
  const { companySettings } = useSelectSettings();
  const { system, getSystemUnit } = useUnits();
  const isLengthBased = isLengthBasedCostPer(costPer);
  const { trackAdjustUnitCost, trackSetUnitCost, trackExport } =
    useCostTableTracking();
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'assetGroupSet', sort: 'asc' },
  ]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [originalData, setOriginalData] =
    useState<CostTableDetailsGridData[]>(propData);

  React.useEffect(() => {
    if (propData.length > 0 && originalData.length === 0) {
      setOriginalData(propData);
    }
  }, [propData, originalData.length]);
  const [updatedValues, setUpdatedValues] = useState<
    Record<string, CostTableDetailsGridData>
  >(propUpdatedValues || {});

  React.useEffect(() => {
    if (propUpdatedValues) {
      setUpdatedValues(propUpdatedValues);
    }
  }, [propUpdatedValues]);
  const [isAdjustCostDialogOpen, setIsAdjustCostDialogOpen] = useState(false);
  const [isFixedValueCostDialogOpen, setIsFixedValueCostDialogOpen] =
    useState(false);
  const apiRef = useGridApiRef();

  const dataWithUpdates = useMemo(() => {
    return propData.map(row => {
      const updates = updatedValues[row.id];
      if (updates) {
        return {
          ...row,
          ...updates,
          isUpdated: true,
        };
      }
      return row;
    });
  }, [propData, updatedValues]);

  const getRowFieldValue = useCallback(
    (row: CostTableDetailsGridData, field: string) => {
      const value = row[field as keyof CostTableDetailsGridData];
      if (field === 'unitCost') {
        return getUnitCostForClientGridOps(
          value as number | null | undefined,
          costPer,
          system
        );
      }
      return value;
    },
    [costPer, system]
  );

  const filteredAndSortedData = useMemo(() => {
    let filteredData = [...dataWithUpdates] as CostTableDetailsGridData[];

    if (searchValue.trim()) {
      const searchLower = searchValue.toLowerCase();
      filteredData = filteredData.filter(
        row =>
          row.assetGroupSet.toLowerCase().includes(searchLower) ||
          row.description.toLowerCase().includes(searchLower)
      );
    }

    if (filterModel.items.length > 0) {
      filteredData = filteredData.filter(row => {
        const checkFilter = (filter: any) => {
          const value = getRowFieldValue(row, filter.field);
          const filterValue = filter.value;

          if (!filterValue) return true;

          switch (filter.operator) {
            case 'contains':
              return String(value)
                .toLowerCase()
                .includes(String(filterValue).toLowerCase());
            case 'equals':
              return value === filterValue;
            case 'startsWith':
              return String(value)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase());
            case 'endsWith':
              return String(value)
                .toLowerCase()
                .endsWith(String(filterValue).toLowerCase());
            case 'is':
              return value === filterValue;
            case 'not':
              return value !== filterValue;
            case '>':
              return Number(value) > Number(filterValue);
            case '>=':
              return Number(value) >= Number(filterValue);
            case '<':
              return Number(value) < Number(filterValue);
            case '<=':
              return Number(value) <= Number(filterValue);
            default:
              return true;
          }
        };

        const logicOperator =
          filterModel.logicOperator || GridLogicOperator.And;

        if (logicOperator === GridLogicOperator.Or) {
          return filterModel.items.some(checkFilter);
        } else {
          return filterModel.items.every(checkFilter);
        }
      });
    }

    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      filteredData.sort((a, b) => {
        const aValue = getRowFieldValue(a, field);
        const bValue = getRowFieldValue(b, field);

        let comparison = 0;
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          comparison = aValue.localeCompare(bValue);
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
          comparison = aValue - bValue;
        }

        return sort === 'desc' ? -comparison : comparison;
      });
    }

    return filteredData;
  }, [dataWithUpdates, searchValue, filterModel, sortModel, getRowFieldValue]);

  const handleRefresh = useCallback(() => {
    setSearchValue('');
    setFilterModel({ items: [] });
    setSortModel([{ field: 'assetGroupSet', sort: 'asc' }]);
    setPaginationModel({ page: 0, pageSize: 10 });
    onRefresh();
  }, [onRefresh]);

  const handleEditUnitCost = useCallback((id: string) => {
    apiRef.current?.startCellEditMode({ id, field: 'unitCost' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apiRef is a stable ref object; adding it would cause unnecessary re-creation
  }, []);

  const handleAdjustCosts = useCallback(
    (percentage: number, isIncrease: boolean) => {
      try {
        const selectedIds = selectedRows.map(String);
        setUpdatedValues(prev => {
          const updated = applyPercentageAdjustment(
            selectedIds,
            propData,
            originalData,
            prev,
            percentage,
            isIncrease
          );
          if (onUpdatedValuesChange) {
            onUpdatedValuesChange(updated);
          }
          return updated;
        });
        dispatch(success(t('Costs adjusted successfully')));
        trackAdjustUnitCost({
          costtableid: costTableId ?? '',
          rowcount: selectedRows.length,
          adjustmentpercent: percentage,
          adjustmenttype: isIncrease ? 'increase' : 'decrease',
        });
      } catch (err) {
        console.error('Error adjusting costs:', err);
        dispatch(error(t('Unable to adjust costs')));
      }
    },
    [
      selectedRows,
      propData,
      originalData,
      onUpdatedValuesChange,
      dispatch,
      t,
      costTableId,
      trackAdjustUnitCost,
    ]
  );

  const handleFixedValueCosts = useCallback(
    (newValue: number) => {
      try {
        const selectedIds = selectedRows.map(String);
        setUpdatedValues(prev => {
          const updated = applyFixedValueUpdate(
            selectedIds,
            propData,
            originalData,
            prev,
            newValue
          );
          if (onUpdatedValuesChange) {
            onUpdatedValuesChange(updated);
          }
          return updated;
        });
        dispatch(success(t('Costs updated in bulk successfully')));
        trackSetUnitCost({
          costtableid: costTableId ?? '',
          rowcount: selectedRows.length,
          value: newValue,
        });
      } catch (err) {
        console.error('Error updating costs:', err);
        dispatch(error(t('Unable to update costs')));
      }
    },
    [
      selectedRows,
      propData,
      originalData,
      onUpdatedValuesChange,
      dispatch,
      t,
      costTableId,
      trackSetUnitCost,
    ]
  );

  const processRowUpdate = useCallback(
    (newRow: CostTableDetailsGridData, oldRow: CostTableDetailsGridData) => {
      const committedUnitCost = isLengthBased
        ? normalizeLengthUnitCostFromUserInput(
            newRow.unitCost,
            system,
            getSystemUnit
          )
        : newRow.unitCost;

      // Both sides are rounded to 2dp (normalize + stored canonical values).
      if (committedUnitCost !== oldRow.unitCost) {
        const originalRow = originalData.find(row => row.id === newRow.id);
        const originalUnitCost = originalRow?.unitCost ?? oldRow.unitCost;

        const isActuallyUpdated = committedUnitCost !== originalUnitCost;
        const rowWithCommittedCost = {
          ...newRow,
          unitCost: committedUnitCost,
        };

        setUpdatedValues(prev => {
          const updated = { ...prev };
          if (isActuallyUpdated) {
            const baseRow = propData.find(row => row.id === newRow.id);
            if (baseRow) {
              updated[newRow.id] = {
                id: baseRow.id,
                assetGroupSet: baseRow.assetGroupSet,
                description: baseRow.description,
                assetCount: baseRow.assetCount,
                unitCost: committedUnitCost,
              };
            }
          } else {
            delete updated[newRow.id];
          }

          if (onUpdatedValuesChange) {
            onUpdatedValuesChange(updated);
          }

          return updated;
        });

        return rowWithCommittedCost;
      }

      // unitCost is the only editable field; preserve oldRow when value unchanged.
      return { ...newRow, unitCost: oldRow.unitCost };
    },
    [
      getSystemUnit,
      isLengthBased,
      onUpdatedValuesChange,
      originalData,
      propData,
      system,
    ]
  );

  const handleProcessRowUpdateError = useCallback(
    (err: Error) => {
      console.error('Error updating row:', err);
      dispatch(error(t('Unable to update cost value')));
    },
    [dispatch, t]
  );

  return (
    <>
      <AdjustCostDialog
        open={isAdjustCostDialogOpen}
        onClose={() => setIsAdjustCostDialogOpen(false)}
        onAdjustCostHandler={handleAdjustCosts}
      />
      <FixedValueCostDialog
        open={isFixedValueCostDialogOpen}
        onClose={() => setIsFixedValueCostDialogOpen(false)}
        onFixedValueUpdate={handleFixedValueCosts}
        isLengthBased={isLengthBased}
      />
      <DataGrid
        loading={loading}
        apiRef={apiRef}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        columns={[
          {
            field: 'assetGroupSet',
            headerName: t('Asset Group Name'),
            renderHeader: () => (
              <Text style={{ fontWeight: 'bold' }}>
                {t('Asset Group Name')}
              </Text>
            ),
            flex: 1,
            type: 'string',
            filterable: true,
            align: 'left',
            headerAlign: 'left',
          },
          {
            field: 'description',
            headerName: t('Description'),
            renderHeader: () => (
              <Text style={{ fontWeight: 'bold' }}>{t('Description')}</Text>
            ),
            flex: 1.5,
            type: 'string',
            filterable: true,
            align: 'left',
            headerAlign: 'left',
          },
          {
            field: 'assetCount',
            headerName: t('Asset Count'),
            renderHeader: () => (
              <Text style={{ fontWeight: 'bold' }}>{t('Asset Count')}</Text>
            ),
            flex: 1,
            type: 'number',
            filterable: true,
            align: 'left',
            headerAlign: 'left',
          },
          {
            field: 'unitCost',
            headerName: isLengthBased
              ? getLengthUnitCostColumnLabel(t, system)
              : t('Unit Cost'),
            renderHeader: () => (
              <Text style={{ fontWeight: 'bold' }}>
                {isLengthBased
                  ? getLengthUnitCostColumnLabel(t, system)
                  : t('Unit Cost')}
              </Text>
            ),
            flex: 1,
            type: 'number',
            filterable: true,
            align: 'left',
            headerAlign: 'left',
            editable: hasEditAccess,
            valueGetter: params =>
              getUnitCostForClientGridOps(
                params.row.unitCost,
                costPer,
                system
              ),
            renderEditCell: (params: GridRenderEditCellParams) => (
              <CurrencyField
                // valueGetter exposes display units; convert to canonical $/m on commit only.
                defaultValue={String(params.value ?? 0)}
                fullWidth
                min={0}
                unit={
                  isLengthBased
                    ? ((getSystemUnit('cost/m') ?? 'cost/m') as 'cost/m')
                    : undefined
                }
                onChange={e => {
                  const raw = e.target.value;
                  const parsed = Number.parseFloat(raw);
                  const value =
                    raw === '' || Number.isNaN(parsed)
                      ? params.value ?? 0
                      : Math.max(0, parsed);
                  params.api.setEditCellValue({
                    id: params.id,
                    field: params.field,
                    value,
                  });
                }}
              />
            ),
            renderCell: params => {
              let displayCost = '';
              if (params.value != null) {
                // valueGetter already returns display units for length-based tables.
                displayCost = getCurrency(params.value, companySettings);
              }
              return (
                <Badge
                  color="primary"
                  variant="dot"
                  invisible={!params.row.isUpdated}>
                  <Text>{displayCost}</Text>
                </Badge>
              );
            },
          },
          ...(hasEditAccess
            ? [
                {
                  field: 'actions',
                  headerName: '',
                  width: 60,
                  sortable: false,
                  filterable: false,
                  align: 'center' as const,
                  headerAlign: 'center' as const,
                  renderCell: (params: GridRenderCellParams) => (
                    <OptionsColumn
                      {...params}
                      onEditUnitCost={handleEditUnitCost}
                    />
                  ),
                },
              ]
            : []),
        ]}
        disableColumnMenu
        disableColumnPinning
        disableColumnReorder
        footerHelper={t('{{from}}-{{to}} of {{total}}', {
          from: Math.min(
            paginationModel.page * paginationModel.pageSize + 1,
            filteredAndSortedData.length
          ),
          to: Math.min(
            (paginationModel.page + 1) * paginationModel.pageSize,
            filteredAndSortedData.length
          ),
          total: filteredAndSortedData.length,
        })}
        onRowSelectionModelChange={setSelectedRows}
        slots={{ toolbar: CustomDataGridHeader }}
        slotProps={{
          toolbar: {
            search: {
              value: searchValue,
              onChange: setSearchValue,
              onCancelSearch: () => setSearchValue(''),
              placeholder: t('Search'),
            },
            filterToolbar: true,
            refreshButton: {
              onRefresh: handleRefresh,
            },
            actionsMenu: hasEditAccess
              ? {
                  disabled: selectedRows.length === 0,
                  menuItems: [
                    {
                      title: t('Adjust Costs (by %)'),
                      onClick: () => setIsAdjustCostDialogOpen(true),
                      disabled: selectedRows.length === 0,
                    },
                    {
                      title: t('Set Unit Cost'),
                      onClick: () => setIsFixedValueCostDialogOpen(true),
                      disabled: selectedRows.length === 0,
                    },
                  ],
                }
              : undefined,
            exportProps: hasEditAccess
              ? {
                  source: 'cost.table.export.create',
                  total: dataWithUpdates.length,
                  disabled: disableExport,
                  requestParams: {
                    export_type: 'cost-table-export',
                    cost_table_id: costTableId,
                    cost_table_name: costTableName || 'cost_table',
                    tenant_id: getTenantId() || '',
                  },
                  messages: {
                    idle: (
                      <>
                        <p>
                          {t(
                            'You can edit the table offline (e.g., in Excel) and then upload it back into the system. Make sure to keep the column and group names the same.'
                          )}
                        </p>
                      </>
                    ),
                  },
                }
              : undefined,
            onExport: hasEditAccess
              ? () =>
                  trackExport({
                    costtableid: costTableId ?? '',
                    rowcount: dataWithUpdates.length,
                  })
              : undefined,
            weave: true,
            loading: loading,
          },
          filterPanel: {
            logicOperators: [GridLogicOperator.And, GridLogicOperator.Or],
          },
        }}
        filterMode="client"
        onFilterModelChange={setFilterModel}
        sortingMode="client"
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        rows={filteredAndSortedData}
        rowCount={filteredAndSortedData.length}
        checkboxSelection
        pagination
        pageSizeOptions={[5, 10, 15, 25, 50, 100]}
        paginationMode="client"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
      />
    </>
  );
};

export default CostTableDetailsGrid;
